import { observer } from 'owa-mobx-react';
import MailListHeaderTabs from './MailListHeaderTabs';
import { getEffectiveFolderDisplayName, isPublicFolder } from 'owa-folders';
import { isFocusedInboxEnabled } from 'owa-mail-triage-common';
import isAttachmentsSearchFolder from 'owa-mail-store/lib/utils/isAttachmentsSearchFolder';
import getSelectedFolder from 'owa-mail-store/lib/utils/getSelectedFolder';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import React from 'react';
import { isFavoritingInProgress } from 'owa-mail-favorites-store';
import { lazyToggleFavoriteFolder } from 'owa-mail-folder-forest-actions';
import { lazyRemovePublicFolderFromFavorites } from 'owa-publicfolder-picker';
import { isFolderInFavorites, isPublicFolderInFavorites } from 'owa-favorites';
import { getMailboxInfoFromTableViewId } from 'owa-mail-mailboxinfo/lib/getMailboxInfo';
import type { UserSettingsStyleSelector } from '../../utils/getStyleSelectorAsPerUserSettings';
import { isCapabilitySupported } from 'owa-capabilities';
import { favoritesCapability } from 'owa-capabilities-definitions/lib/favoritesCapability';
import {
    getStore,
    type MailFolderTableQuery,
    isCatchUpFilter,
    isHeadlinesFilter,
    isNeedsReplyView,
    isCopilotResultsTable,
    getCopilotResultsOriginTableViewId,
} from 'owa-mail-list-store';
import loc from 'owa-localize';
import {
    catchUpHeaderLabel,
    headlinesHeaderLabel,
    needsReplyViewLabel,
} from './MailListFolderHeaderSecondRowContent.locstring.json';
import { setItem } from 'owa-local-storage';
import type { MailFolder } from 'owa-graph-schema';
import shouldShowUnreadPivot from '../../utils/shouldShowUnreadPivot';

export interface MailListFolderHeaderSecondRowContentProps {
    tableViewId: string;
    containerCssClass: string;
    mailListHeaderStylesAsPerUserSettings?: UserSettingsStyleSelector;
}

const defaultExport: React.FunctionComponent<MailListFolderHeaderSecondRowContentProps> = observer(
    function MailListFolderHeaderSecondRowContent(
        props: MailListFolderHeaderSecondRowContentProps
    ) {
        const selectedFolder = getSelectedFolder();
        const isInboxSelected = folderIdToName(selectedFolder?.id ?? '') === 'inbox';
        const mailboxInfo = getMailboxInfoFromTableViewId(props.tableViewId);

        const tableView = getStore().tableViews.get(props.tableViewId);
        const folderTableView = isCopilotResultsTable(tableView?.tableQuery)
            ? (getStore().tableViews.get(getCopilotResultsOriginTableViewId(mailboxInfo) ?? '') ??
              tableView)
            : tableView;
        const folderTableQuery = folderTableView?.tableQuery as MailFolderTableQuery;
        const isHeadlinesFilterSelected = isHeadlinesFilter(folderTableQuery);
        const isCatchUpOrHeadlinesFilterSelected =
            isCatchUpFilter(folderTableQuery) || isHeadlinesFilterSelected;
        const isInNeedsReplyView = isNeedsReplyView(folderTableQuery);

        const isInboxWithFocusedPivots =
            isInboxSelected &&
            isFocusedInboxEnabled(mailboxInfo) &&
            !isCatchUpOrHeadlinesFilterSelected &&
            !isInNeedsReplyView;

        const showUnreadPivot = shouldShowUnreadPivot(tableView);

        // If no pivots are rendered in the Inbox folder - and Priority pivot is not shown -
        // reset the value for "last selected pivot" to "not-priority" so we don't
        // load the "High Priority" filter when the next session begins.
        if (isInboxSelected && !isInboxWithFocusedPivots) {
            setItem(window, `mail_LAST_SELECTED_PIVOT${mailboxInfo.userIdentity}`, 'not-priority');
        }

        if (!selectedFolder) {
            /**
             * This function gets called when when user switches from a favorite persona to a favorite category. At that time, the selected node
             * is set to category node while the table is still persona table. Thus we are expecting the folder to be undefined as the category folder
             * is not saved in folder store.
             */
            return null;
        }
        const selectedFolderId = selectedFolder?.id;

        // We don't currently support adding attachments search folders or PST folders
        const shouldShowFavoriteToggle =
            !isCatchUpOrHeadlinesFilterSelected &&
            !isAttachmentsSearchFolder(selectedFolderId) &&
            isCapabilitySupported(favoritesCapability, mailboxInfo) &&
            !isInNeedsReplyView;

        const isFolderInFavorite =
            !!selectedFolderId &&
            (isPublicFolder(selectedFolderId)
                ? isPublicFolderInFavorites(selectedFolderId)
                : isFolderInFavorites(selectedFolderId));

        return (
            <MailListHeaderTabs
                tableViewId={props.tableViewId}
                containerCssClass={props.containerCssClass}
                mailListHeaderStylesAsPerUserSettings={props.mailListHeaderStylesAsPerUserSettings}
                isInboxWithFocusedPivots={isInboxWithFocusedPivots}
                showUnreadPivot={showUnreadPivot}
                headerText={getDisplayName(
                    isCatchUpOrHeadlinesFilterSelected,
                    isHeadlinesFilterSelected,
                    isInNeedsReplyView,
                    selectedFolder
                )}
                showFavoriteToggle={shouldShowFavoriteToggle}
                onFavoriteToggleClick={toggleFavoriteState}
                isInFavorites={isFolderInFavorite}
                mailboxInfo={mailboxInfo}
                tableView={folderTableView}
            />
        );
    },
    'MailListFolderHeaderSecondRowContent'
);
export default defaultExport;

const getDisplayName = (
    isCatchUpOrHeadlinesFilterSelected: boolean,
    isHeadlinesFilterSelected: boolean,
    isInNeedsReplyView: boolean,
    selectedFolder: MailFolder | null | undefined
): string => {
    if (isCatchUpOrHeadlinesFilterSelected) {
        return isHeadlinesFilterSelected ? loc(headlinesHeaderLabel) : loc(catchUpHeaderLabel);
    }

    if (isInNeedsReplyView) {
        return loc(needsReplyViewLabel);
    }

    return getEffectiveFolderDisplayName(selectedFolder);
};

const toggleFavoriteState = async (evt: React.MouseEvent<unknown>) => {
    evt.stopPropagation();
    const selectedFolder = getSelectedFolder();
    const selectedFolderId = selectedFolder?.id;

    if (!selectedFolder || !selectedFolderId) {
        return;
    }
    if (isFavoritingInProgress(selectedFolderId)) {
        // Do nothing: favoriting is in progress
        return;
    }

    const isFolderInFavorite =
        !!selectedFolderId &&
        (isPublicFolder(selectedFolderId)
            ? isPublicFolderInFavorites(selectedFolderId)
            : isFolderInFavorites(selectedFolderId));

    if (isPublicFolder(selectedFolderId)) {
        const removePublicFolderFromFavorites = await lazyRemovePublicFolderFromFavorites.import();
        removePublicFolderFromFavorites(selectedFolder);
    } else {
        lazyToggleFavoriteFolder.importAndExecute(
            selectedFolderId,
            !!isFolderInFavorite,
            'FolderHeader'
        );
    }
};
